import type { FC } from "react";

import dayjs from "dayjs";

import { dayAndShortMonth, shortMonthAndShortYear } from "@similarweb/pro-logic-components";
import Sentry from "@similarweb/sentry-logger";

import type { ISupportedLanguagesAndLocale } from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import { getLanguagesForCampaigns } from "app/components/campaigns/shared/locationsAndLanguagesUtils";
import {
    SHARE_OF_VOICE_EXCLUDE_OTHERS,
    SHARE_OF_VOICE_INCLUDE_OTHERS,
} from "app/components/campaigns/shared/overview/constants";
import { ERankTrackerPerformanceOvertimeTabs } from "app/components/campaigns/shared/overview/types";
import { getOverviewGraphYAxisFilter } from "app/components/campaigns/shared/overview/utils";
import Legends from "app/components/React/Legends/Legends";
import type { ILegendProps } from "app/components/React/Legends/legendsTypes";
import { GraphWidget } from "app/components/widget/widget-types/GraphWidget";
import type { DatesRange } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/types/common";
import { DefaultFetchService } from "app/services/fetchService";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import { RankTrackerOverviewPerformanceGraphWidgetFilters } from "../widget-filters/RankTrackerOverviewPerformanceGraphWidgetFilters";
import { COLOR_OF_SECONDARY_SITES, SECONDARY_SITES_KEY } from "../widget-utilities/Campaigns/campaigns.consts";
import {
    getAllCampaigns,
    getDataAccordingToGrarnularity,
    getDurationForURLWithCampaignMax,
    getDurationToPresent,
    getFiltersTooltip,
    getRTDurationObjectForCampaign,
    getSelectedLocationsForURL,
    getUpdateLocationsOnViewData,
    transformAndCapitalizeTexts,
} from "../widget-utilities/Campaigns/CampaignsUtils";

const fetchService = DefaultFetchService.getInstance();

function createDataFetcherFactory(widget) {
    return {
        fetch: async () => {
            const { campaignguid, country, from, to, sites, tags, excludeTags, withUntagged, untaggedFilter } =
                widget.apiParams;

            const apiParams = {
                scrapingConfigurationGuids: country,
                isDaily: true,
                campaignguid,
                sites,
                from,
                to,
                tags,
                excludeTags,
                untaggedFilter: untaggedFilter ?? (withUntagged === "true" ? "include" : undefined),
            };

            return fetchService.get(`${widget._metricConfig.endpoint}/Graph`, apiParams);
        },
    };
}

export type DataPoint = { Key: string; Value: string };
export interface ITransformedData {
    Data: { [key: string]: { [key: string]: DataPoint[] } };
}

const RankTrackerOverviewPerformanceGraphLegends: FC<ILegendProps> = (props) => {
    return (
        <div>
            <Legends {...props} />
        </div>
    );
};

export const getLegendsWithSecondarySites = (currentCampaign) => {
    const mainSite = {
        id: currentCampaign?.site,
        image: currentCampaign?.asset,
        name: currentCampaign?.site,
    };
    const competitors =
        currentCampaign?.competitors?.map(({ site, asset }) => ({
            id: site,
            name: site,
            image: asset,
        })) ?? [];
    const secondarySites = {
        id: SECONDARY_SITES_KEY,
        name: SECONDARY_SITES_KEY,
        image: "",
        color: COLOR_OF_SECONDARY_SITES,
        hidden: true,
    };
    return [mainSite, ...competitors, secondarySites];
};

export const enrichWithSecondarySitesData = (supportingData, timeGranularity: string) => {
    const secondarySitesData = getDataAccordingToGrarnularity(supportingData.othersShareOfVoice.data, timeGranularity);
    return {
        Others: secondarySitesData?.map(({ key, value }) => ({ Key: key, Value: value })) ?? [],
    };
};

export class RankTrackerOverviewPerformanceGraphWidget extends GraphWidget {
    private parsedLanguages: ISupportedLanguagesAndLocale[];
    reactLegendComponent: FC<ILegendProps>;

    constructor() {
        super();
        (async () => {
            await this.initCampaigns();
        })();
        this.reactLegendComponent = RankTrackerOverviewPerformanceGraphLegends;
    }

    private initCampaigns = async () => {
        this.parsedCampaigns = await getAllCampaigns();
    };

    static getWidgetDashboardType() {
        return "Graph";
    }

    static getWidgetResourceType() {
        return "Graph";
    }

    static getWidgetMetadataType() {
        return "RankTrackerOverviewPerformanceGraph";
    }

    public isPptSupported = () => false;

    private dataFetcherFactory = () => {
        return {
            create: createDataFetcherFactory,
        };
    };

    public getFetcherFactory() {
        return this.dataFetcherFactory();
    }

    static getFiltersComponent() {
        return RankTrackerOverviewPerformanceGraphWidgetFilters;
    }

    public validateData(data: any): boolean {
        // Check if at least one value is an object with a non-empty array
        return Object.values(data).some(
            (value) => typeof value === "object" && Object.values(value)?.[0].some((dataList) => !!dataList),
        );
    }

    getLegendItems(data?: any) {
        if (this._params.graphTab !== ERankTrackerPerformanceOvertimeTabs.SHARE_OF_VOICE) {
            return this._widgetConfig.properties.key;
        }

        const currentCampaign = this._widgetConfig.properties.currentCampaignConfiguration;
        return getLegendsWithSecondarySites(currentCampaign);
    }

    public getProUrl() {
        const { campaignguid, webSource, graphTab, duration, timeGranularity } = this._params;
        const { currentCampaignConfiguration } = this._widgetConfig.properties;
        if (!currentCampaignConfiguration) {
            return super.getProUrl();
        }

        const urlParams = {
            graphTab,
            device: webSource,
            campaignId: campaignguid,
            graphGranularity: timeGranularity,
            duration: getDurationForURLWithCampaignMax(duration, currentCampaignConfiguration),
            locationsIds: getSelectedLocationsForURL(
                this._widgetConfig.properties.country,
                currentCampaignConfiguration?.scrapingConfigurations,
            ),
        };

        return this._swNavigator.href(this._metricConfig.state, urlParams);
    }

    private transformData(response: any): ITransformedData {
        try {
            let reducedData;
            const { records, supportingData } = response;
            const { timeGranularity } = this._params;

            reducedData = records.reduce((acc, record) => {
                const { site: key, ...rest } = record;
                let tabRecordsProperty = this.apiParams.graphTab;
                if (this.apiParams.graphTab === ERankTrackerPerformanceOvertimeTabs.SHARE_OF_VOICE) {
                    tabRecordsProperty = this.apiParams.includeOtherShareOfVoice
                        ? SHARE_OF_VOICE_INCLUDE_OTHERS
                        : SHARE_OF_VOICE_EXCLUDE_OTHERS;
                }
                const data = rest[tabRecordsProperty];
                const transformedData = getDataAccordingToGrarnularity(data, timeGranularity);

                return {
                    ...acc,
                    [key]: {
                        [key]: transformedData?.map(({ key, value }) => ({ Key: key, Value: value })) ?? [],
                    },
                };
            }, {});

            if (this._params.graphTab === ERankTrackerPerformanceOvertimeTabs.SHARE_OF_VOICE) {
                reducedData = {
                    ...reducedData,
                    Others: enrichWithSecondarySitesData(supportingData, timeGranularity),
                };
            }

            return { Data: reducedData };
        } catch (error) {
            Sentry.error(`Error parsing graph data - Overview report dashboard widget`, error);
        }
    }

    public async onNewDataReceived(response: any) {
        const transformedData = this.transformData(response);
        await super.onNewDataReceived(transformedData);
    }

    public async callbackOnGetData(response: any): Promise<void> {
        // using the campaign configuration to guarantee the correct order of the legends
        const { site: mainSite, competitors } = this._widgetConfig.properties.currentCampaignConfiguration;
        const comparedItemKeys = [mainSite, ...competitors.map((c) => c.site)];
        if (this._params.graphTab === ERankTrackerPerformanceOvertimeTabs.SHARE_OF_VOICE) {
            comparedItemKeys.push(SECONDARY_SITES_KEY);
        }

        await super.callbackOnGetData(response, comparedItemKeys);
    }

    async getChartSeries(unorderedData: any, compareItemsKeys?: string[]) {
        const series = [...(await super.getChartSeries(unorderedData, compareItemsKeys))];
        if (series.find((s) => s.name === SECONDARY_SITES_KEY)) {
            const secondarySites = series.find((s) => s.name === SECONDARY_SITES_KEY);
            secondarySites.color = COLOR_OF_SECONDARY_SITES;
            secondarySites.visible = false;
        }
        return series;
    }

    getHighChartsConfig(chartOptions, formattedData, data) {
        const { timeGranularity } = this._params;
        const yAxisFilter = getOverviewGraphYAxisFilter(this.apiParams.graphTab);

        const xAxisformat = timeGranularity === GranularityNames.MONTHLY ? shortMonthAndShortYear : dayAndShortMonth;
        const xAxisFormatter = ({ value }) => dayjs(value).format(xAxisformat);
        chartOptions.xAxisTickInterval = undefined;

        chartOptions = {
            ...chartOptions,
            viewOptions: { ...chartOptions.viewOptions, showMarkersOnSingle: true },
            timeGranularity,
            xAxisLabelFormatter: xAxisFormatter,
            tooltipFormatFunc: yAxisFilter,
            yAxis: {
                min: 0,
                minPadding: 0,
                tickLength: 20,
                startOnTick: true,
                tickPositions: null,
                showFirstLabel: true,
                tickPixelInterval: 50,
                reversed: this._params.graphTab === ERankTrackerPerformanceOvertimeTabs.AVG_WEIGHTED_POSITION,
                labels: {
                    formatter() {
                        return (yAxisFilter as (val: string | number) => string | number)(this.value);
                    },
                },
                xAxis: {
                    type: "datetime",
                },
            },
        };

        if (this._params.graphTab === ERankTrackerPerformanceOvertimeTabs.VISIBILITY) {
            chartOptions.yAxisMax = 1;
        }

        if (this._params.graphTab === ERankTrackerPerformanceOvertimeTabs.AVG_WEIGHTED_POSITION) {
            chartOptions = {
                ...chartOptions,
                tooltipFormatParam: 1,
            };
        }

        if (this._params.graphTab === ERankTrackerPerformanceOvertimeTabs.SHARE_OF_VOICE) {
            // The tooltipPointTransformer is required to sum the tooltip values to 100% based
            // on the current visible series.
            chartOptions = {
                ...chartOptions,
                plotOptions: { area: { stacking: "percent", connectNulls: false } },
                stacked: true,
                showStacked: true,
                tooltipFormatParam: 2,
                tooltipPointTransformer: (y, points) => {
                    const totalSum = points.reduce((acc, num) => acc + num.y, 0);
                    return (y / totalSum) * 100;
                },
                yAxis: {
                    ...chartOptions.yAxis,
                    max: 100,
                    reversedStacks: true,
                },
            };
        }

        return super.getHighChartsConfig(chartOptions, formattedData, data);
    }

    private updateLocationsOnViewData() {
        const webSource = this._widgetConfig.properties.webSource;
        if (webSource) {
            this.viewData.country = getUpdateLocationsOnViewData(this.parsedLanguages, this._widgetConfig.properties);
        }
    }

    getViewData() {
        super.getViewData();
        if (!this.parsedLanguages) {
            getLanguagesForCampaigns(false).then((languages) => {
                this.parsedLanguages = languages;
                this.updateLocationsOnViewData();
            });
        } else {
            this.updateLocationsOnViewData();
        }
        const webSource = this._widgetConfig.properties.webSource;
        if (!this._widgetConfig.properties.country && !webSource) {
            //in case devices was not selected yet
            return this.viewData;
        }
        const duration = this._widgetConfig.properties.duration;
        const currentCampaign = this._widgetConfig.properties.currentCampaignConfiguration;
        const durationObject: DatesRange = getRTDurationObjectForCampaign(duration, currentCampaign);
        this.viewData.duration = getDurationToPresent(duration, durationObject);
        const subtitleTextItem = getFiltersTooltip(this._params);
        if (subtitleTextItem) {
            this.viewData.subtitleTextItem = transformAndCapitalizeTexts(subtitleTextItem);
        }
        return this.viewData;
    }
}

RankTrackerOverviewPerformanceGraphWidget.register();
